import React, { useState, useEffect, createContext } from 'react';
import { ethers } from 'ethers';
import { Web3Provider } from '@ethersproject/providers';
import Onboard from 'bnc-onboard';
import { API } from 'bnc-onboard/dist/src/interfaces';
import { infuraKey, bncDappId, defaultChainId } from 'utils/config';
import { getItem, removeItem, setItem } from 'utils/localStorage';
import { logEvent } from 'utils/analytics';
import Notify from 'bnc-notify';
import { getChecksummedAddress } from 'utils/ethAddress';
import { getNetworkDetails, walletConnectRPCs } from 'utils/networks';

export enum WalletTypes {
  metamask = 'MetaMask',
  walletConnect = 'WalletConnect',
}

interface WalletContextValue {
  onboard: API | null;
  notify: any;
  web3Provider: Web3Provider | null;
  address: string | null;
  network: number | null;
  selectWallet(): any;
  logoutWallet(): any;
  switchNetwork(arg1: any): any;
  getWallet(): any;
  isMetaMask(): any;
}

const wallets: any[] = [];

export const setupWallets = () => {
  if (infuraKey) {
    wallets.push({
      walletName: 'walletConnect',
      rpc: walletConnectRPCs,
    });
  }
};

const WalletContext = createContext<WalletContextValue>({
  onboard: null,
  notify: null,
  web3Provider: null,
  address: null,
  network: null,
  selectWallet: () => {},
  logoutWallet: () => {},
  switchNetwork: () => {},
  getWallet: () => {},
  isMetaMask: () => {},
});

const WalletProvider = ({ children }: any) => {
  const [web3Provider, setWeb3Provider] = useState<any>(null);
  const [address, setAddress] = useState<string | null>(null);
  const [network, setNetwork] = useState<number | null>(null);

  // -----------------------------------------------------------------------------------
  // Initialize onboard and notify library
  // -----------------------------------------------------------------------------------
  // note: we are not currently doing anything with a user's balance
  // if we wanted to, there is a userWallet that we can use and keep updated
  // via onbard.getState()

  const [onboard, setOnboard] = useState<API | null>(null);
  const [notify, setNotify] = useState<any>(null);

  // note: onboard requires a chainId to be set but we support multiple
  useEffect(() => {
    initialize(defaultChainId);
  }, []);

  const initialize = (networkId: number) => {
    const onboard = Onboard({
      networkId,
      dappId: bncDappId,
      hideBranding: true,
      subscriptions: {
        wallet: wallet => {
          if (wallet.name) {
            logEvent('select_wallet', {
              event_category: 'Wallet',
              event_label: `Selecting wallet: ${wallet.name}`,
              name: wallet.name,
            });
          }

          if (wallet.provider) {
            const ethersProvider = new ethers.providers.Web3Provider(wallet.provider, 'any');
            setWeb3Provider(ethersProvider);

            if (wallet.provider.chainId) {
              setNetwork(wallet.provider.chainId);
            }

            // store user preference
            // note: only for metamask to prevent any weird issues
            if (wallet.name === WalletTypes.metamask) {
              setItem('selectedWallet', wallet.name);
            }
          } else {
            // logging out
            setWeb3Provider(null);
            removeItem('selectedWallet');
          }
        },
        address: address => (address ? setAddress(getChecksummedAddress(address)) : ''),
        network: async newNetworkId => {
          setNetwork(newNetworkId);
        },
      },
      walletSelect: {
        wallets: [
          ...wallets,
          { walletName: 'metamask', preferred: true, display: { mobile: false, desktop: true } },
        ],
      },
      walletCheck: [{ checkName: 'connect' }],
    });

    const notify = Notify({
      networkId,
      dappId: bncDappId,
      darkMode: true,
    });

    setOnboard(onboard);
    setNotify(notify);
  };

  // -----------------------------------------------------------------------------------
  // Wallet utility functions
  // -----------------------------------------------------------------------------------
  const selectWallet = React.useCallback(async () => {
    const walletSelected = await onboard?.walletSelect();

    if (walletSelected) {
      await onboard?.walletCheck();
    }
  }, [onboard]);

  const logoutWallet = React.useCallback(async () => {
    setAddress(null);
    return onboard?.walletReset();
  }, [onboard]);

  const getWallet = React.useCallback(() => {
    return onboard?.getState();
  }, [onboard]);

  const isMetaMask = React.useCallback(() => {
    const state = getWallet();
    return state?.wallet?.name === WalletTypes.metamask;
  }, [getWallet]);

  // -----------------------------------------------------------------------------------
  // Load the previous connected wallet so returning users have nice experience
  // only happens once right after the onboard module is initialized
  // -----------------------------------------------------------------------------------
  useEffect(() => {
    const previouslySelectedWallet = getItem('selectedWallet');

    if (previouslySelectedWallet && onboard) {
      logEvent('load_wallet', {
        event_category: 'Wallet',
        event_label: `Loading previously selected wallet: ${previouslySelectedWallet}`,
        name: previouslySelectedWallet,
      });

      onboard.walletSelect(previouslySelectedWallet);
    }
  }, [onboard]);

  // -----------------------------------------------------------------------------------
  // Switch network
  // -----------------------------------------------------------------------------------
  // note: this only works if connected to MetaMask
  const switchNetwork = React.useCallback(
    async (networkId: number) => {
      if (!web3Provider) return;

      const state = onboard?.getState();
      if (!state) return;
      const { wallet } = state;

      // note: coudldn't get this to work on WalletConnect
      if (wallet.name !== WalletTypes.metamask) return;

      try {
        // @ts-ignore
        await web3Provider.provider.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: ethers.utils.hexValue(networkId) }],
        });
        setNetwork(networkId);
      } catch (switchError: any) {
        // This error code indicates that the chain has not been added to MetaMask.
        if (switchError.code === 4902) {
          const networkDetails = getNetworkDetails(networkId);

          if (!networkDetails) {
            throw new Error('Unsupported network');
          }
          try {
            // @ts-ignore
            await web3Provider.provider.request({
              method: 'wallet_addEthereumChain',
              params: [networkDetails],
            });
            // setNetwork(networkId);
          } catch (addError) {
            console.debug(addError);
            // throw addError;
            // handle "add" error
          }
        }
      } // handle other "switch" errors
      // }
    },
    [web3Provider, onboard]
  );

  return (
    <WalletContext.Provider
      value={{
        onboard,
        web3Provider,
        address,
        network,
        selectWallet,
        logoutWallet,
        notify,
        switchNetwork,
        getWallet,
        isMetaMask,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
};

const useWallet = () => {
  const context = React.useContext(WalletContext);
  if (context === undefined) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};

export { WalletProvider, useWallet };
